// ── Aviso de cupo anual de vacaciones (web) ────────────────────────────────
// Lo usan AprobacionesPage (al aprobar una solicitud de vacaciones) y
// AssignEventModal (al asignar vacaciones desde la ficha). La parte PURA
// (checkVacationCap / vacationCapMessage en src/lib/dayStatus.js, compartida
// con la app) usa el MISMO cómputo que los contadores del calendario y de la
// ficha: emparejado de periodos, festivos del trabajador, histórico de
// horarios y reparto por año (un periodo que cruza el cambio de año cuenta en
// cada año sus días). Aquí solo se cargan los datos.
//
// NO bloquea: devuelve el resultado y cada pantalla decide (confirmación
// explícita del admin). Si algún dato imprescindible no se puede cargar
// (eventos, horario) devuelve `status: 'error'` con un aviso: NUNCA se cae a
// "horario vacío" (antes eso inflaba o anulaba el cómputo en silencio).

import { getEventsByRange, getWorkHours, listAgreements, EVENT_TYPES } from './api';
import { fetchEnrichedScheduleHistory } from '../hooks/useWorkerHourBalance';
import { buildEnrichedScheduleHistory, scheduleByDateInput } from '../lib/scheduleHistory';
import { fetchHolidaysSetForWorker } from '../lib/spainHolidays';
import { yearCountWindow } from '../lib/vacationCounter';
import {
  pairEventsIntoPeriods, checkVacationCap, vacationCapMessage, vacationHolidayTypeFor,
} from '../lib/dayStatus';
import { timeZoneForWorker, dayKeyInZone } from '../lib/workerTime';

const holidayWorker = (worker) => ({
  autonomousCommunity: worker?.autonomousCommunity || worker?.community || null,
  province: worker?.province || null,
  locality: worker?.locality || null,
  companyId: worker?.companyId ?? null,
});

// Años (del trabajador) que toca el periodo solicitado.
const yearsOfRequest = (request, tz) => {
  const s = Number(String(dayKeyInZone(request.start, tz) || '').slice(0, 4));
  const e = Number(String(dayKeyInZone(request.end || request.start, tz) || '').slice(0, 4));
  if (!Number.isFinite(s) || !s) return [];
  const out = [];
  for (let y = s; y <= (Number.isFinite(e) && e >= s ? e : s); y += 1) out.push(y);
  return out;
};

// Comprueba el cupo de `worker` si se aprueba / asigna `request`
// ({ start, end, startId?, endId? }, instantes ISO).
// Devuelve:
//   { status: 'skipped' }                      → sin convenio / sin cupo.
//   { status: 'error', message }               → no se pudo comprobar (aviso).
//   { status: 'ok', checks: [{ year, ...checkVacationCap }], message|null }
//     `message` = texto del aviso si algún año se pasa (null si no).
// `agreements` opcional (lista ya cargada); si no, se piden.
export const loadVacationCapCheck = async ({ worker, request, agreements = null, verb = 'aprobar' }) => {
  if (!worker?.id || !request?.start) return { status: 'skipped' };
  let ags = agreements;
  if (!Array.isArray(ags) || ags.length === 0) {
    try { ags = await listAgreements(); } catch { return { status: 'error', message: 'No se pudo cargar el convenio del trabajador para comprobar el cupo de vacaciones.' }; }
  }
  const ag = (Array.isArray(ags) ? ags : []).find((a) => Number(a.id) === Number(worker.agreement)) || null;
  const allowance = Number(ag?.vacationDays) || 0;
  if (!ag || allowance <= 0) return { status: 'skipped' };

  const tz = timeZoneForWorker(worker);
  const years = yearsOfRequest(request, tz);
  if (years.length === 0) return { status: 'skipped' };
  const first = yearCountWindow(years[0]);
  const last = yearCountWindow(years[years.length - 1]);

  const [evRes, whRes, histRes, holRes] = await Promise.allSettled([
    getEventsByRange(worker.id, first.fetchFrom, last.fetchTo),
    getWorkHours(worker.id),
    fetchEnrichedScheduleHistory(worker.id),
    fetchHolidaysSetForWorker(holidayWorker(worker), [years[0] - 1, ...years]),
  ]);
  if (evRes.status === 'rejected') {
    return { status: 'error', message: 'No se pudieron cargar las vacaciones del trabajador: no se ha comprobado el cupo anual.' };
  }
  if (whRes.status === 'rejected') {
    return { status: 'error', message: 'No se pudo cargar el horario del trabajador: no se ha comprobado el cupo anual.' };
  }
  const workhours = Array.isArray(whRes.value) ? whRes.value : [];
  // El histórico es best-effort (igual que en la ficha): sin él se usa el
  // horario actual.
  const history = histRes.status === 'fulfilled' && Array.isArray(histRes.value) ? histRes.value : [];
  const schedule = scheduleByDateInput(buildEnrichedScheduleHistory(history, workhours), workhours);
  const holidaysSet = holRes.status === 'fulfilled' && holRes.value?.set ? holRes.value.set : new Set();

  const events = Array.isArray(evRes.value) ? evRes.value : [];
  const periods = pairEventsIntoPeriods(events, EVENT_TYPES.VACATION_START, EVENT_TYPES.VACATION_END);
  const holidayType = vacationHolidayTypeFor(ag);

  const checks = years.map((year) => {
    const { from, to } = yearCountWindow(year);
    return {
      year,
      ...checkVacationCap({
        periods, request, allowance, holidayType, workHoursByDow: schedule, holidaysSet, timeZone: tz, from, to,
      }),
    };
  });
  if (checks.some((c) => c.scheduleUnknown)) {
    return { status: 'error', message: 'El trabajador no tiene horario configurado: no se puede calcular el cupo de vacaciones.' };
  }
  const messages = checks
    .map((c) => vacationCapMessage(c, { verb, year: c.year }))
    .filter(Boolean);
  return { status: 'ok', checks, message: messages.length ? messages.join('\n\n') : null };
};
